import { UploadActions, UploadItem, UploadStatus } from '@app/types/document';
import axios from 'axios';
import { useState } from 'react';
import { v4 as uuid } from 'uuid';

type useUploadManagerProps = {
  config?: {
    isMultiFile?: boolean;
  };
};

/**
 * Hook for managing file uploads with support for both single and multi-file modes
 *
 * @param props - Configuration object
 * @param props.config.isMultiFile - If true, allows multiple files. If false (default), only allows one file at a time
 *
 * @example
 * // Single file upload (default)
 * const uploadManager = useUploadManager();
 * uploadManager.actions.add(singleFile); // Can pass single File
 *
 * @example
 * // Multi-file upload
 * const uploadManager = useUploadManager({ config: { isMultiFile: true } });
 * uploadManager.actions.add(fileList); // Can pass FileList or File[]
 */
export const useUploadManager = (props: useUploadManagerProps = {}) => {
  const { config } = props;
  const isMultiFile = config?.isMultiFile ?? false;
  const [items, setItems] = useState<UploadItem[]>([]);

  const add = (files: File | FileList | File[]) => {
    // Convert input to array format
    const fileArray = Array.isArray(files)
      ? files
      : files instanceof FileList
      ? Array.from(files)
      : [files]; // Single File

    const uploadDetails = fileArray.map((file) => {
      return {
        id: uuid(),
        file: file,
        progress: 0,
        status: UploadStatus.INIT,
        cancelTokenSource: axios.CancelToken.source(),
      };
    });

    setItems((prev) => {
      if (isMultiFile) {
        return [...prev, ...uploadDetails];
      } else {
        return uploadDetails.slice(0, 1);
      }
    });
  };

  const update = (id: string, file: File) => {
    setItems((prev) => {
      const instances = [...prev];
      const index = instances.findIndex((item) => item.id === id);
      if (index !== -1) {
        instances[index] = { ...instances[index], file };
      }
      return instances;
    });
  };

  const updateProgress = (id: string, progress: number, status?: UploadStatus) => {
    setItems((prev) => {
      const instances = [...prev];
      const index = instances.findIndex((item) => item.id === id);
      if (index !== -1) {
        instances[index] = {
          ...instances[index],
          progress,
          ...(status && { status }),
        };
      }
      return instances;
    });
  };

  const remove = (id: string) => {
    setItems((prev) => {
      const instances = [...prev];
      const index = instances.findIndex((item) => item.id === id);
      if (index !== -1) {
        instances.splice(index, 1);
      }
      return instances;
    });
  };

  const cancel = (id: string) => {
    const index = items.findIndex((item) => item.id === id);
    if (index === -1 || items[index].progress === 100) {
      // If item not found or already completed, do nothing
      return;
    }

    const instance = items[index];
    instance.cancelTokenSource.cancel('Upload cancelled by user');
    remove(instance.id);
  };

  const cancelAll = () => {
    items.forEach((instance) => {
      cancel(instance.id);
    });
  };

  const actions: UploadActions = {
    add,
    update,
    updateProgress,
    cancel,
    cancelAll,
  };

  return {
    isMultiFile,
    item: items[0] || null,
    items: items,
    hasFiles: items.length > 0,
    actions,
  };
};
